import { IBatchTranscriptionClientResult } from '@1js/al-voice-client';
import { FileUploadEntryPoints, FILE_UPLOAD_MAX_FILE_SIZE_BYTES, RECOVERY_FLOW_MAX_FILE_SIZE_BYTES } from '../../../AppConstants';
import { LoadingActionTypes, LoadingStatus} from '../../../frontend/common/enums/loadingEnums'
import Dispatcher from '../../../frontend/dispatcher';
import { NetworkError, NetworkErrors } from '../../../utils/Errors';
import { IsFileContentTypeSupportedForUpload, IsFileFormatSupportedForUpload } from '../../../utils/FileUploadSupportedFormat';
import { GetLanguageFromLocale } from '../../../utils/TranscriptionLanguages';
import { IsFileNameValidForOneDriveUpload } from '../../../utils/utils';
import { FileUploadForegroundTaskError, FileUploadForegroundTaskErrors } from '../fileUpload/FileUploadForegroundTaskErrors';
import { FrontDoorFileUploadError, FrontDoorFileUploadErrors } from '../../frontDoor/FrontDoorFileUploadErrors';
import { ITranscription, ITranscriptionSegment } from '../../interfaces/ITranscriptionManager';
import { GetNumericSpeakerIdFromSpeakerId, secFrom100nsec } from '../../utils/Utils';

export function SetLoadingPercentDone(percentDone: number | null) {
  Dispatcher.dispatch({
      type: LoadingActionTypes.SetPercentDoneAction,
      payload: percentDone
  });
}

export function SetLoadingStatus(status: LoadingStatus) {
  Dispatcher.dispatch({
      type: LoadingActionTypes.SetStatusAction,
      payload: status
  });
}

// Rescale a percentage in range [0, 1] to [start, end].
// i.e. 0 will map to start, 1 will map to end and, anything in between will be scaled.
export function RescalePercent(percent: number, start: number, end: number) {
  return percent * (end - start) + start;
}

export function GetFileContentTypeFileUpload(file: File) {
  if (IsFileContentTypeSupportedForUpload(file.type)) {
      return file.type;
  }

  return 'audio/mpeg';
}

// Check if file valid for upload
export function CheckIfFileValidToUpload(file: File, fileUploadEntryPoint: FileUploadEntryPoints ) {
  const fileSizeUploadMaxFileSize = fileUploadEntryPoint == FileUploadEntryPoints.RECOVERING? RECOVERY_FLOW_MAX_FILE_SIZE_BYTES: FILE_UPLOAD_MAX_FILE_SIZE_BYTES;

  if (file.size <= 0) {
    throw new FileUploadForegroundTaskError(FileUploadForegroundTaskErrors.InvalidEmptyFile);
  }

  if (file.size > fileSizeUploadMaxFileSize) {
    if (fileUploadEntryPoint == FileUploadEntryPoints.RECOVERING) {
      throw new FileUploadForegroundTaskError(FileUploadForegroundTaskErrors.RecoveryFlowFileExceedsSizeLimit);
    }
    throw new FileUploadForegroundTaskError(FileUploadForegroundTaskErrors.FileExceedsSizeLimit);
  }

  const fileExtension: string | undefined = file.name.split('.').pop();
  if (!IsFileFormatSupportedForUpload(fileExtension?.toLowerCase())) {
    throw new FileUploadForegroundTaskError(FileUploadForegroundTaskErrors.UnsupportedFileFormat);
  }

  if (!IsFileNameValidForOneDriveUpload(file.name)) {
    throw new FileUploadForegroundTaskError(FileUploadForegroundTaskErrors.InvalidFileNameForOneDriveFileUpload);
  }
}

// Some browsers (e.g. Safari) do not support the promisified version of decodeAudioData, so this function
// is used to create a promise.
/*export function getDecodeAudioDataPromise(arrayBuffer: ArrayBuffer, audioContext: any): Promise<AudioBuffer> {
    return new Promise((resolve, reject) => {
      audioContext.decodeAudioData(arrayBuffer, resolve, reject);
    });
}*/

/*
// Gets the duration of the passed audio file
export function GetAudioFileDuration(file: File): Promise<number> {
    LogEvents.FileUpload.BeginDurationMeasure(DurationMeasureType.Normal, FileUploadKeys.GetFileDurationDurationSeconds);
    const fileReader = new FileReader();

    const promise = new Promise<number>((resolve, reject) => {
        fileReader.onload = async () => {
        try {
            const packets = <ArrayBuffer>fileReader.result;
            // Create OfflineAudioContext with length 1.
            // This is OfflineAudioContext instead of AudioContext because we don't need to render audio to an output device.
            // Length 1 is enough because we are only using it for audio decoding.
            const audioContext = new OfflineAudioContext(1 /*numberOfChannels*///, 1 /*length*/, 16000 /* sample rate */);
            /*const audioBuffer: AudioBuffer = await getDecodeAudioDataPromise(packets, audioContext);
            LogEvents.FileUpload.EndDurationMeasure(FileUploadKeys.GetFileDurationDurationSeconds);
            resolve(audioBuffer.duration);
        } catch (e) {
            reject(e);
        }
        };

        fileReader.onabort = fileReader.onerror = () => {
        reject(new Error('fileReader.onerror'));
        };
    });

    fileReader.readAsArrayBuffer(file);
    return promise;
}*/

export function GetTranscriptionFromBatchTranscriptionResult(batchTranscriptionResult: IBatchTranscriptionClientResult, locale: string) {
    const transcript: ITranscription = {
        transcriptSegments: [],
    };

    let maxSpeakerId: number | null = null;

    batchTranscriptionResult.transcriptSegments.forEach((transcriptSegment: any) => {
        const startTimestamp = secFrom100nsec(transcriptSegment.offsetInTicks);
        const endTimestamp = startTimestamp + secFrom100nsec(transcriptSegment.durationInTicks);
        const speakerId = GetNumericSpeakerIdFromSpeakerId(transcriptSegment.userId);

        if (speakerId != null) {
            maxSpeakerId = maxSpeakerId == null ? speakerId : Math.max(speakerId, maxSpeakerId);
        }

        transcript.transcriptSegments.push({
            text: transcriptSegment.text,
            language: GetLanguageFromLocale(locale),
            start: startTimestamp,
            end: endTimestamp,
            speakerId
        });
    });

    transcript.speakerNames = new Array(maxSpeakerId != null ? maxSpeakerId + 1 : 0);

    // We need to sort the segments because speedy CTS may return them out of order due to paralellization on their side.
    transcript.transcriptSegments.sort((segment1: ITranscriptionSegment, segment2: ITranscriptionSegment) => {
        return segment1.start - segment2.start;
    });

    return transcript;
}

// Convert from FrontDoorFileUpload errors to a publicly-facing error
export function PublicErrorFromFrontDoorUploadError(e: FrontDoorFileUploadError) {
    switch (e.name) {
        case FrontDoorFileUploadErrors.UnsupportedFileFormat:
            return new FileUploadForegroundTaskError(FileUploadForegroundTaskErrors.UnsupportedFileFormat);

        case FrontDoorFileUploadErrors.NetworkError:
            return new NetworkError(NetworkErrors.NoInternetConnection);

        default:
            return e;
    }
}